import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (same convention as ar.ts / journal-entry.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Status enum — DRAFT / PENDING / APPROVED / PAID / REJECTED / CANCELLED
// ============================================================
export const PaymentRequestStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'APPROVED',
    'PAID',
    'REJECTED',
    'RETURNED',
    'CANCELLED',
]);
// ============================================================
// Approval rows (child)
// ============================================================
export const PaymentRequestApprovalSchema = z.object({
    id: z.uuid(),
    requestId: z.uuid(),
    stepNo: z.number().int().positive(),
    roleCode: z.string().min(1),
    roleLabel: z.string().min(1),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// ============================================================
// PaymentRequest header
// ============================================================
export const PaymentRequestSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    prDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    categoryCode: z.string().min(1),
    description: z.string().min(1),
    amount: DecimalLike,
    vatRate: DecimalLike,
    vatAmount: DecimalLike,
    whtRate: DecimalLike,
    whtAmount: DecimalLike,
    netAmount: DecimalLike,
    totalAmount: DecimalLike,
    paymentTerms: z.string().nullable().optional(),
    paymentMethod: z.string().nullable().optional(),
    status: PaymentRequestStatusSchema,
    currentStep: z.number().int().nonnegative(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    rejectReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    cashAccountCode: z.string().nullable().optional(),
    paidAt: z.coerce.date().nullable().optional(),
    paidBy: z.string().nullable().optional(),
    jv1EntryId: z.uuid().nullable().optional(),
    jv2EntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row enrichment — display names + counts for table rendering
export const PaymentRequestListRowSchema = PaymentRequestSchema.extend({
    vendorName: z.string(),
    branchName: z.string().nullable().optional(),
    approvalChainLength: z.number().int().nonnegative(),
    // CURRENT step's required roleCode while PENDING (null otherwise) — drives the
    // list-row approve-button role gate without loading the detail chain. submittedBy
    // (SoD half) is already carried on PaymentRequestSchema.
    currentApproverRole: z.string().nullable().optional(),
});
// Detail — full header + chain rows
export const PaymentRequestWithApprovalsSchema = PaymentRequestListRowSchema.extend({
    approvals: z.array(PaymentRequestApprovalSchema),
});
// ============================================================
// Wire shapes — POST bodies
// ============================================================
// Create — DRAFT row. voucherNo + tax math is server-derived.
export const CreatePaymentRequestSchema = z.object({
    prDate: z.iso.date(),
    dueDate: z.iso.date(),
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    categoryCode: z.string().min(1),
    description: z.string().min(1),
    amount: z.coerce.number().positive(),
    vatRate: z.coerce.number().min(0).max(100).default(7),
    whtRate: z.coerce.number().min(0).max(100).default(0),
    paymentTerms: z.string().optional(),
    paymentMethod: z.string().optional(),
});
// M7 Phase 3 (SoD-by-actorId): the optional `sodOverrideReason` carries the
// audited justification when a holder of `approval.sodoverride` lifts a
// self-approval block (the single-qualified-approver deadlock escape valve).
// The central engine gates + audits on it; the schema must ACCEPT it (plain
// optional, NOT .strict()) or nestjs-zod strips it from the body and the
// override is unreachable.
export const ApprovePaymentRequestSchema = z.object({
    step: z.coerce.number().int().positive(),
    comment: z.string().optional(),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const RejectPaymentRequestSchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
// Return-for-revision — PENDING → RETURNED (resubmittable), same shape as reject.
export const ReturnPaymentRequestSchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const PayPaymentRequestSchema = z.object({
    // COA code of the bank/cash account funding the payout. OPTIONAL: when omitted
    // the service resolves BANK_PRIMARY (the company's configured default cash
    // account, set in /settings/account-mapping).
    cashAccountCode: z.string().trim().min(2).optional(),
    paidAt: z.iso.date().optional(),
});
//# sourceMappingURL=payment-request.js.map